// Tiny UI sound layer (expo-audio). Four synthesized chimes generated by
// scripts/make-sounds.js — played on task-complete, streak-up, level-up, and
// badge-unlock so the existing haptic/confetti moments land harder.
//
// expo-audio is REQUIRED LAZILY (same pattern as expo-store-review in
// index.tsx): the native module isn't in dev builds made before it was added,
// so a static import would crash them. Until the next EAS build, every call
// here is a silent no-op.
//
// Respects the iOS mute switch (playsInSilentMode: false) — celebration
// chimes should never blast through a deliberately silenced phone.
import type { AudioPlayer } from 'expo-audio';

let ExpoAudio: {
  createAudioPlayer: (source: unknown) => AudioPlayer;
  setAudioModeAsync: (mode: { playsInSilentMode: boolean }) => Promise<void>;
} | null = null;
try { ExpoAudio = require('expo-audio'); } catch { ExpoAudio = null; }

const SOURCES = {
  task: require('../assets/sounds/task.wav'),
  streak: require('../assets/sounds/streak.wav'),
  level: require('../assets/sounds/level.wav'),
  badge: require('../assets/sounds/badge.wav'),
} as const;

export type SoundName = keyof typeof SOURCES;

const players: Partial<Record<SoundName, AudioPlayer>> = {};
let ready = false;

// Call once at app start. Safe to call again (no-op). Failures are swallowed —
// sound is garnish, never worth blocking or crashing anything over.
export async function initSounds(): Promise<void> {
  if (ready || !ExpoAudio) return;
  try {
    await ExpoAudio.setAudioModeAsync({ playsInSilentMode: false });
    for (const name of Object.keys(SOURCES) as SoundName[]) {
      players[name] = ExpoAudio.createAudioPlayer(SOURCES[name]);
    }
    ready = true;
  } catch (e) {
    console.warn('Sound init failed (continuing silently):', e);
  }
}

export function playSound(name: SoundName): void {
  const p = players[name];
  if (!p) return;
  try {
    // expo-audio doesn't auto-rewind finished players — seek first so the
    // same chime can fire repeatedly.
    p.seekTo(0);
    p.play();
  } catch {
    // never let a sound failure break the interaction it decorates
  }
}
